/*
 * Project: bellcloud
 * File: apps/frontend/src/components/ui/common/PhoneInput/PhoneInput.tsx
 * Description: E.164 standard phone number input component with country selector
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { usePhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import 'react-international-phone/style.css';
import { normalizePhone } from './utils';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useTranslation } from 'react-i18next';

interface PhoneInputProps {
  label?: string;
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  defaultCountry?: string;
  preferredCountries?: string[];
  showExtension?: boolean;
  extension?: string;
  onExtensionChange?: (value: string) => void;
  id?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const Container = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]}px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[1]}px;
`;

const InputWrapper = styled.div<{ $hasError?: boolean; $size?: 'sm' | 'md' | 'lg' }>`
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]}px;
  align-items: center;
`;

const StyledPhoneInput = styled.input<{ $hasError?: boolean; $size?: 'sm' | 'md' | 'lg' }>`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[3]}px`};
  outline: none;
  transition: all 0.2s ease;
  min-width: 0;

  ${({ $size = 'md' }) => {
    const sizeMap = { sm: 'height: 32px;', md: 'height: 40px;', lg: 'height: 48px;' };
    return sizeMap[$size];
  }}

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.indicator};
    box-shadow: 0 0 0 2px
      ${({ theme, $hasError }) =>
        $hasError
          ? `${theme.colors.status.danger.stroke.border}33`
          : `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

// ─── 커스텀 국가 선택기 ───────────────────────────────────────────

const SelectorButton = styled.button<{ $hasError?: boolean; $size?: 'sm' | 'md' | 'lg' }>`
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  padding: ${({ theme }) => `0 ${theme.spacing[3]}px`};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${({ $size = 'md' }) => {
    const sizeMap = { sm: 'height: 32px;', md: 'height: 40px;', lg: 'height: 48px;' };
    return sizeMap[$size];
  }}

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.surface.control.emphasis};
    border-color: ${({ theme }) => theme.colors.border.indicator};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DropdownArrow = styled.span<{ $open: boolean }>`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[3]}px`};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.container};
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const CountryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const CountryItem = styled.li<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[3]}px`};
  cursor: pointer;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.surface.control.emphasis : 'transparent'};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.control.emphasis};
  }
`;

const CountryName = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DialCode = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
`;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => `${theme.spacing[4]}px ${theme.spacing[3]}px`};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
`;

const SelectorWrapper = styled.div`
  position: relative;
`;

interface CustomCountrySelectorProps {
  selectedIso2: string;
  onSelect: (iso2: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  size?: 'sm' | 'md' | 'lg';
  preferredCountries?: string[];
}

// ISO2 코드 → 국기 이모지 (Unicode Regional Indicator)
const getFlagEmoji = (iso2: string): string =>
  iso2.toUpperCase().replace(/./g, ch => String.fromCodePoint(127397 + ch.charCodeAt(0)));

const allCountries = defaultCountries.map(parseCountry);

const CustomCountrySelector: React.FC<CustomCountrySelectorProps> = ({
  selectedIso2,
  onSelect,
  disabled,
  hasError,
  size = 'md',
  preferredCountries = [],
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = allCountries.find(c => c.iso2 === selectedIso2);

  // 클릭 외부 감지 → 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 열릴 때 검색창 포커스
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  // LIKE 검색: 국가명 또는 국가코드로 필터링
  const filtered = useCallback(() => {
    const q = search.trim().toLowerCase().replace(/^\+/, '');
    const preferred = allCountries.filter(c => preferredCountries.includes(c.iso2));
    const rest = allCountries.filter(c => !preferredCountries.includes(c.iso2));
    const sorted = [...preferred, ...rest];

    if (!q) return sorted;
    return sorted.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.iso2.toLowerCase().includes(q)
    );
  }, [search, preferredCountries]);

  const handleSelect = (iso2: string) => {
    onSelect(iso2);
    setOpen(false);
    setSearch('');
  };

  const countries = filtered();

  return (
    <SelectorWrapper ref={wrapperRef}>
      <SelectorButton
        type="button"
        $hasError={hasError}
        $size={size}
        disabled={disabled}
        onClick={() => !disabled && setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ fontSize: 20 }}>{selected ? getFlagEmoji(selected.iso2) : '🌐'}</span>
        <span style={{ fontSize: 13 }}>+{selected?.dialCode ?? ''}</span>
        <DropdownArrow $open={open}>▼</DropdownArrow>
      </SelectorButton>

      {open && (
        <DropdownContainer role="listbox">
          <SearchInput
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="select"
          />
          <CountryList>
            {countries.length === 0 ? (
              <EmptyMessage>검색 결과 없음</EmptyMessage>
            ) : (
              countries.map(c => (
                <CountryItem
                  key={c.iso2}
                  $selected={c.iso2 === selectedIso2}
                  onClick={() => handleSelect(c.iso2)}
                  role="option"
                  aria-selected={c.iso2 === selectedIso2}
                >
                  <span style={{ fontSize: 18 }}>{getFlagEmoji(c.iso2)}</span>
                  <CountryName>{c.name}</CountryName>
                  <DialCode>+{c.dialCode}</DialCode>
                </CountryItem>
              ))
            )}
          </CountryList>
        </DropdownContainer>
      )}
    </SelectorWrapper>
  );
};

// 국제 형식 기준 국가별 최대 국번 자릿수
// disableDialCodeAndPrefix: true 모드에서는 라이브러리가 로컬 형식(trunk 포함) 기준으로
// 최대 길이를 허용하므로, 국제 형식(trunk 제거 후) 기준으로 maxLength를 별도 강제
const NATIONAL_DIGIT_MAX: Record<string, number> = {
  kr: 10, // 10-XXXX-XXXX
  jp: 10, // 90-XXXX-XXXX (mobile)
  gb: 10, // 7XXX-XXXXXX
  de: 12, // 15X-XXXXXXXX (mobile, ~11-12)
  fr: 9, // 6-XX-XX-XX-XX
  nl: 9, // 6-XXXXXXXX
  be: 9, // 4XX-XX-XX-XX
  au: 9, // 4XX-XXX-XXX
  nz: 9, // 2X-XXX-XXXX
  us: 10, // XXX-XXX-XXXX
  ca: 10, // XXX-XXX-XXXX
  cn: 11, // 1XX-XXXX-XXXX
  in: 10, // 9XXXX-XXXXX
  id: 11, // 8XX-XXXX-XXXX
  ph: 10, // 9XX-XXX-XXXX
  th: 9, // 8X-XXX-XXXX
  vn: 9, // 9X-XXXX-XXXX
  tr: 10, // 5XX-XXX-XXXX
  pk: 10, // 3XX-XXX-XXXX
  ir: 10, // 9XX-XXX-XXXX
  at: 11, // varies
  ch: 9, // 7X-XXX-XXXX
  pl: 9, // XXX-XXX-XXX
  pt: 9, // 9X-XXX-XXXX
  ie: 9, // 8X-XXX-XXXX
  fi: 10, // 4XX-XXX-XXXX
  hu: 9, // XX-XXX-XXXX
  ro: 9, // 7X-XXX-XXXX
  ua: 9, // 9X-XXX-XXXX
};

// 국가 iso2 + dialCode 길이 기반 국제 형식 최대 자릿수 반환
const getNationalMax = (iso2: string, dialCodeLen: number): number =>
  NATIONAL_DIGIT_MAX[iso2] ?? Math.min(15 - dialCodeLen, 12);

// ─────────────────────────────────────────────────────────────────

const ExtensionInput = styled.input<{ $hasError?: boolean; $size?: 'sm' | 'md' | 'lg' }>`
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[3]}px`};
  outline: none;
  transition: all 0.2s ease;
  width: 100px;
  min-width: 80px;

  ${({ $size = 'md' }) => {
    const sizeMap = {
      sm: 'height: 32px;',
      md: 'height: 40px;',
      lg: 'height: 48px;',
    };
    return sizeMap[$size];
  }}

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.danger.stroke.border : theme.colors.border.indicator};
    box-shadow: 0 0 0 2px
      ${({ theme, $hasError }) =>
        $hasError
          ? `${theme.colors.status.danger.stroke.border}33`
          : `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

const ExtensionLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  white-space: nowrap;
`;

const MessageContainer = styled.div`
  position: relative;
  min-height: 20px;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.status.danger.stroke.border};
  font-size: ${({ theme }) => theme.fontSize.xs}px;
  margin-top: ${({ theme }) => theme.spacing[1]}px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]}px;
`;

const HintMessage = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.xs}px;
  margin-top: ${({ theme }) => theme.spacing[1]}px;
  font-style: italic;
`;

/**
 * E.164 표준을 따르는 전화번호 입력 컴포넌트
 *
 * 특징:
 * - 국가 코드 선택 드롭다운 (국기 아이콘 포함)
 * - 자동 포맷팅 (국가별 전화번호 형식)
 * - E.164 형식으로 저장 (+821012345678)
 * - 실시간 유효성 검사
 * - 내선번호 입력 지원 (showExtension prop)
 * - 복사/붙여넣기 자동 처리
 *
 * @example
 * // 기본 사용 (휴대폰)
 * <PhoneInput
 *   value={phone}
 *   onChange={(value, isValid) => setPhone(value)}
 *   defaultCountry="kr"
 * />
 *
 * @example
 * // 내선번호 포함 (유선전화)
 * <PhoneInput
 *   value={phone}
 *   onChange={(value, isValid) => setPhone(value)}
 *   showExtension
 *   extension={ext}
 *   onExtensionChange={setExt}
 *   defaultCountry="kr"
 * />
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  hint,
  defaultCountry = 'kr',
  preferredCountries = ['kr', 'us', 'jp', 'cn'],
  showExtension = false,
  extension = '',
  onExtensionChange,
  id,
  name,
  size = 'md',
  fullWidth = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation();
  // controlled value: 외부 value와 타이핑 중 trunk prefix(0) 정규화를 모두 처리
  const [internalPhone, setInternalPhone] = useState(() => normalizePhone(value));

  useEffect(() => {
    setInternalPhone(normalizePhone(value));
  }, [value]);

  const { phone, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry,
    preferredCountries,
    value: internalPhone,
    disableDialCodeAndPrefix: true,
    onChange: ({ phone: newPhone }) => {
      const normalized = normalizePhone(newPhone);
      // trunk prefix(0)가 제거된 경우 라이브러리 내부 상태도 갱신
      if (normalized !== newPhone) {
        setInternalPhone(normalized);
      }
      const e164Regex = /^\+[1-9]\d{1,14}$/;
      onChange(normalized, e164Regex.test(normalized));
    },
  });

  // 표시값: 정규화된 phone에서 국가코드 제거 → 순수 번호만
  const dialPrefix = country?.dialCode ? `+${country.dialCode}` : '+';
  const normalizedPhone = normalizePhone(phone);
  const nationalPart = normalizedPhone.startsWith(dialPrefix)
    ? normalizedPhone.slice(dialPrefix.length)
    : '';
  const displayValue = nationalPart;

  // 국제 형식 기준 최대 자릿수 (라이브러리가 로컬 형식 기준으로 1자리 더 허용하는 것을 방지)
  const maxNationalDigits = getNationalMax(country?.iso2 ?? '', country?.dialCode?.length ?? 2);

  const inputId =
    id || (label ? `phone-input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  return (
    <Container $fullWidth={fullWidth}>
      {label && <Label htmlFor={inputId}>{label}</Label>}

      <InputWrapper $hasError={!!error} $size={size}>
        <CustomCountrySelector
          selectedIso2={country.iso2}
          onSelect={iso2 => setCountry(iso2)}
          disabled={disabled}
          hasError={!!error}
          size={size}
          preferredCountries={preferredCountries}
        />

        <StyledPhoneInput
          ref={inputRef}
          id={inputId}
          name={name || inputId}
          type="tel"
          value={displayValue}
          onChange={handlePhoneValueChange}
          placeholder={placeholder ?? t(i18nKeys.common.enterPhoneNumber)}
          disabled={disabled}
          $hasError={!!error}
          $size={size}
          maxLength={maxNationalDigits}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />

        {showExtension && (
          <>
            <ExtensionLabel>ext.</ExtensionLabel>
            <ExtensionInput
              type="text"
              value={extension}
              onChange={e => onExtensionChange?.(e.target.value)}
              placeholder={t(i18nKeys.common.extension)}
              disabled={disabled}
              $hasError={!!error}
              $size={size}
              maxLength={10}
            />
          </>
        )}
      </InputWrapper>

      <MessageContainer>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!error && hint && isFocused && <HintMessage>{hint}</HintMessage>}
      </MessageContainer>
    </Container>
  );
};

